import { useEffect, useMemo, useState } from "react";
import {
  charRoster,
  charAnimKeys,
  charFilled,
  CHAR_GROUPS,
  heroBaseKey,
  heroOwnedKeys,
  isCustomHeroBase,
  nextHeroBaseId,
  type CharDef,
  type AssetSlot,
} from "@greg-chick/shared";
import {
  loadManifest,
  loadSlots,
  publishAssets,
  type Manifest,
  type ManifestEntry,
} from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { SlotUploader } from "./SlotUploader";
import { heroDeletePrompt, HERO_DIRS } from "./HeroMaker";
import { AnimationStudio } from "./AnimationStudio";
import { OverlayFitPanel } from "./OverlayFitPanel";
import { WorldSizePanel } from "./WorldSizePanel";
import { NpcLinePanel } from "./NpcLinePanel";
import { NpcAgentPanel } from "./NpcAgentPanel";
import { Msg } from "./ui";

/**
 * 🧸 캐릭터 — 한 친구를 고르면 그 친구에 대한 모든 것을 여기서 고친다(FR-ADMIN).
 *
 * 예전엔 그레그 하나를 고치는 데 화면 네 곳을 돌아다녀야 했다:
 *   그림=[그림 전체 목록] · 걷기=[움직임 만들기] · 키=[캐릭터 크기] · 말=[NPC 대사].
 * "어느 화면에 뭐가 있더라"를 외워야 하는 구조였고, 직접꾸미기 룩 16종은 아예 어느 화면에도
 * 없었다. 여기서는 **누구를(캐릭터) → 무엇을(그림/움직임/키/말)** 순서로만 고르면 된다.
 *
 * 원래 네 화면 중 [그림 전체 목록]·[움직임 만들기]는 도구·고급에 그대로 남겨 뒀다 —
 * 여기 안 나오는 자리(배경·타일·파츠 등)를 손볼 길은 있어야 한다.
 */

const TABS = [
  { id: "art", icon: "🖼️", label: "그림", hint: "가만히 서 있는 모습" },
  { id: "anim", icon: "🎞️", label: "움직임", hint: "걸어다닐 때" },
  { id: "size", icon: "📏", label: "키", hint: "얼마나 크게 보일지" },
  { id: "talk", icon: "💬", label: "말", hint: "무슨 말을 할지" },
  { id: "deco", icon: "🎩", label: "모자·소품", hint: "머리·손에 얹는 것 자리 맞추기" },
  { id: "know", icon: "🐱", label: "아는 것", hint: "무엇을 물어보면 답할지" },
] as const;
type TabId = (typeof TABS)[number]["id"];

const DIR_LABEL: Record<string, string> = { down: "앞모습", up: "뒷모습", side: "옆모습" };

/** 스트립(가로로 이어붙인 그림)의 0번 칸만 보여주는 썸네일. 정지 그림은 그대로. */
function Thumb({ url, strip, alt }: { url?: string; strip?: boolean; alt: string }) {
  const [frames, setFrames] = useState(1);
  useEffect(() => {
    if (!url || !strip) return;
    let alive = true;
    const img = new Image();
    img.onload = () => alive && setFrames(Math.max(1, Math.round(img.width / img.height)));
    img.src = url;
    return () => {
      alive = false;
    };
  }, [url, strip]);
  if (!url) return <span className="chub__thumb chub__thumb--empty" aria-label={`${alt} 그림 없음`} />;
  return (
    <span
      className="chub__thumb"
      role="img"
      aria-label={alt}
      style={{
        backgroundImage: `url(${url})`,
        backgroundSize: strip ? `${frames * 100}% 100%` : "contain",
        backgroundPosition: "0 0",
      }}
    />
  );
}

export function CharacterHub({ onGoAssets }: { onGoAssets?: () => void }) {
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  const [slots, setSlots] = useState<AssetSlot[]>([]);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  // 새로고침해도 보던 자리로 — 한 캐릭터를 여러 번 손볼 때 매번 다시 찾는 건 고역이다
  const [pick, setPick] = useState(() => localStorage.getItem("gc_admin_char") ?? "npc.greg_white");
  const [tab, setTab] = useState<TabId>(() => (localStorage.getItem("gc_admin_char_tab") as TabId) || "art");
  const [q, setQ] = useState("");

  useEffect(() => {
    void loadManifest().then(setManifest).catch((e: Error) => setErr(e.message));
    void loadSlots().then(setSlots).catch(() => setSlots([]));
  }, []);

  const a = manifest.assets as Record<string, ManifestEntry>;
  const slotByKey = useMemo(() => Object.fromEntries(slots.map((s) => [s.key, s])), [slots]);

  // 마을 친구 목록: 정해둔 셋 + 매니페스트에서 발견한 새 친구(관리자가 npc.<id>.down 을 올리면 저절로 나온다)
  const npcs = useMemo(() => {
    const known = [
      { id: "greg_white", label: "그레그" },
      { id: "chick", label: "병아리" },
      { id: "greg_cat", label: "검은 고양이" },
    ];
    const ids = new Set(known.map((n) => n.id));
    for (const k of Object.keys(a)) {
      const m = /^npc\.([a-z0-9_]+)\./.exec(k);
      if (m?.[1] && !ids.has(m[1])) {
        ids.add(m[1]);
        known.push({ id: m[1], label: a[`npc.${m[1]}.down`]?.alt || m[1] });
      }
    }
    return known;
  }, [a]);

  /** 지금 만드는 중인 빈 자리 — 목록에서 감춘 자리도 이때만 잠깐 보인다. */
  const [making, setMaking] = useState<string | null>(null);

  const roster = useMemo(
    () =>
      charRoster(
        npcs,
        [
          { id: "greg", label: "왼쪽 (책 읽는 그레그)" },
          { id: "chick", label: "오른쪽 (병아리)" },
        ],
        // **그림이 실제로 올라온 키**만 넘긴다 — 운영자가 만든 캐릭터는 여기서 발견된다.
        // 만드는 중인 자리 하나는 아직 그림이 없어도 끼워 넣는다(안 그러면 올릴 곳이 안 보인다).
        [
          ...Object.keys(a).filter((k) => a[k]?.url),
          ...(making ? [heroBaseKey(making)] : []),
        ],
      ).map((c) => {
        // 운영자가 이름을 붙였으면 그 이름으로 — "직접 만든 친구 1" 은 자리 이름일 뿐이다.
        const alt = c.stillKey ? a[c.stillKey]?.alt?.trim() : undefined;
        return alt ? { ...c, label: alt } : c;
      }),
    [npcs, a, making],
  );

  const cur = roster.find((c) => c.key === pick) ?? roster[0]!;
  /**
   * 지금 고른 것이 **완성 캐릭터**인가 — 맞으면 그 id(지우기를 붙인다).
   *
   * 기본 제공(explorer_a~e)도 지울 수 있다. 다만 **되돌리는 무게가 다르다** —
   * 운영자가 만든 건 올린 파일을 다시 올리면 되지만, 기본 제공은 그림이 저장소에서 온
   * 것이라 새로 만들어야 한다. 그래서 물어보는 말이 다르다.
   */
  const heroPick = (() => {
    const id = cur.key.startsWith("hero.base.") ? cur.key.slice("hero.base.".length) : "";
    return id ? { id, builtin: !isCustomHeroBase(id) } : null;
  })();
  const choose = (key: string) => {
    setPick(key);
    localStorage.setItem("gc_admin_char", key);
  };
  const chooseTab = (t: TabId) => {
    setTab(t);
    localStorage.setItem("gc_admin_char_tab", t);
  };

  /**
   * 새 자리를 하나 열어 그림 탭으로 — **몇 명까지라는 제한은 없다.**
   * id 는 안 쓰는 가장 작은 번호를 자동으로 잡는다(nextHeroBaseId). 운영자가 id 를 고를
   * 이유가 없다 — 아이에게 보이는 건 그림과 이름뿐이다.
   */
  const startNewHero = () => {
    const free = nextHeroBaseId(Object.keys(a));
    setMaking(free);
    choose(heroBaseKey(free));
    chooseTab("art");
  };

  /** 지우기 — **무엇이 사라지는지 먼저 보여준다**(만들기 화면과 같은 말, 같은 함수). */
  const removeHero = (id: string, builtin = false) => {
    // 지워지는 목록은 [➕ 직접 만들기]와 **같은 함수**로 만든다(shared) — 한쪽만 새 자리를
    // 알면 지운 캐릭터의 옆·뒷모습이 매니페스트에 남고, 아무 화면에도 안 나와서 아무도 모른다.
    const present = heroOwnedKeys(heroBaseKey(id), (k) => !!a[k]?.url, HERO_DIRS);
    const nick = a[heroBaseKey(id)]?.alt || id;
    if (!window.confirm(heroDeletePrompt(nick, present.map((d) => d.label), builtin))) return;
    present.forEach((d) => setEntry(d.key, null));
    if (making === id) setMaking(null);
    choose("avatar.player");
  };

  const setEntry = (key: string, entry: ManifestEntry | null) => {
    setManifest((m) => {
      const next = { ...m.assets };
      if (entry) next[key] = entry;
      else delete next[key];
      return { ...m, assets: next };
    });
    setDirty((d) => new Set(d).add(key));
  };

  async function publish() {
    const out = await publishAssets(manifest.assets);
    setManifest((m) => ({ ...m, version: out.version }));
    setDirty(new Set());
  }
  useRegisterSave({
    id: "chars",
    label: "캐릭터 그림",
    dirty: dirty.size,
    save: publish,
    // 💾 바꾼 그림 자리만 담는다 — 그림 파일 자체는 올릴 때 이미 서버에 있다.
    //    지운 자리는 null 로 남긴다(키를 빼면 "안 지운 것"이 된다).
    draft: {
      data: () => ({
        entries: Object.fromEntries([...dirty].map((k) => [k, a[k] ?? null])),
        dirty: [...dirty],
      }),
      restore: (d) => {
        const v = d as { entries?: Record<string, ManifestEntry | null>; dirty?: string[] };
        if (!v?.entries) return;
        setManifest((m) => {
          const next = { ...m.assets };
          for (const [k, e] of Object.entries(v.entries!)) {
            if (e) next[k] = e;
            else delete next[k];
          }
          return { ...m, assets: next };
        });
        setDirty(new Set(v.dirty ?? Object.keys(v.entries!)));
      },
    },
  });

  // 검색: 이름으로 거른다. 운영자는 키가 아니라 "병아리"를 안다.
  const term = q.trim().toLowerCase();
  const shown = term ? roster.filter((c) => c.label.toLowerCase().includes(term) || c.key.includes(term)) : roster;

  // 이 캐릭터에서 못 쓰는 탭은 이유를 적어 막는다(빈 화면보다 낫다)
  const tabBlocked = (t: TabId): string | null => {
    if (t === "art" && !cur.stillKey) return "이 친구는 걷기 그림의 0번 칸이 곧 서 있는 모습이에요. [🎞️ 움직임]에서 올려요";
    if (t === "size" && cur.group === "main") return "첫 화면 캐릭터 크기는 [🏠 홈 화면]에서 정해요";
    if (t === "talk" && !cur.npcId) return "말은 마을을 돌아다니는 친구만 해요";
    // 도슨트는 **마을을 돌아다니는 친구**에게만 붙인다 — 아이 아바타·첫 화면 캐릭터는
    // 누를 수 있는 대화 상대가 아니다(누르면 대화창이 뜨는 건 npc 오브젝트뿐이다).
    if (t === "know" && !cur.npcId) return "아는 것은 마을을 돌아다니는 친구만 정해요";
    /**
     * 모자·소품은 **아이가 직접 꾸미는 캐릭터**에만 얹는다(완성형 프리셋·마을 친구엔 안 얹는다).
     *
     * 🔴 예전 조건은 `hero.look.` 으로 시작하는 캐릭터를 요구했는데 **목록에 그런 항목이 없다**
     * (`charRoster` 가 내놓는 건 `npc.*`·`avatar.player`·`hero.base.*`·`landing.char.*` 뿐이다).
     * 그래서 이 탭은 **누구를 골라도 열리지 않았다** — 안에 든 자리 맞추기 화면이 통째로 죽어 있었다.
     * 그 화면은 아이(b1~b4)·옷(o0~o3)을 **스스로 고르므로** 룩 캐릭터를 따로 고를 필요가 없다.
     */
    if (t === "deco" && !cur.isHero) {
      return "모자·소품은 아이 캐릭터에만 얹어요 — 위에서 [기본 아바타]를 골라 주세요";
    }
    if (t === "deco" && cur.key.startsWith("hero.base.")) {
      return "완성 캐릭터는 그 자체로 완성이라 모자를 안 얹어요. [기본 아바타]를 골라 주세요";
    }
    return null;
  };
  const blocked = tabBlocked(tab);

  return (
    <section className="panel chub">
      {err ? <Msg text={`⚠️ 목록을 불러오지 못했어요 — ${err}`} /> : null}

      {/* ── ① 누구를 고칠까 ── */}
      <div className="chub__pickbar">
        <h3 className="chub__h">① 친구 고르기</h3>
        <input
          className="chub__search"
          value={q}
          placeholder="이름으로 찾기 (예: 병아리)"
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {CHAR_GROUPS.map((g) => {
        const items = shown.filter((c) => c.group === g.id);
        if (!items.length) return null;
        return (
          <div key={g.id} className="chub__group">
            <h4 className="chub__gh">
              {g.icon} {g.label} <small>{g.hint}</small>
            </h4>
            <div className="chub__grid">
              {items.map((c) => {
                const [filled, total] = charFilled(c, (k) => !!a[k]?.url);
                const thumbKey = c.stillKey && a[c.stillKey]?.url ? c.stillKey : `${c.key}.down`;
                return (
                  <button
                    key={c.key}
                    type="button"
                    className="chub__card"
                    data-on={c.key === cur.key || undefined}
                    onClick={() => choose(c.key)}
                  >
                    <Thumb url={a[thumbKey]?.url} strip={thumbKey !== c.stillKey} alt={c.label} />
                    <b>{c.label}</b>
                    <small data-none={filled === 0 || undefined}>
                      {filled === 0 ? "아직 그림 없음" : `그림 ${filled}/${total}`}
                    </small>
                  </button>
                );
              })}
              {/* 빈 자리를 카드로 늘어놓는 대신 **만들기 하나**. 자리 제한이 없어져서
                  「몇 칸 남음」도 없앴다 — 남은 칸을 세어 보여주면 없는 제한을 있는 것처럼 만든다. */}
              {g.id === "hero" && !q.trim() ? (
                <button type="button" className="chub__card chub__card--new" onClick={startNewHero}>
                  <span className="chub__newmark" aria-hidden="true">＋</span>
                  <b>새 친구 만들기</b>
                  <small>몇 명이든 만들 수 있어요</small>
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      {shown.length === 0 ? <p className="hint">그런 이름의 친구가 없어요.</p> : null}

      {/* ── ② 무엇을 고칠까 ── */}
      <h3 className="chub__h chub__h--2">
        ② <b>{cur.label}</b> 의 무엇을 고칠까요
      </h3>
      {heroPick ? (
        <p className="chub__own">
          {heroPick.builtin ? "처음부터 들어 있던 캐릭터예요." : "직접 만든 친구예요."}
          <button
            type="button"
            className="btn btn--sm btn--danger"
            onClick={() => removeHero(heroPick.id, heroPick.builtin)}
          >
            🗑️ 이 친구 지우기
          </button>
        </p>
      ) : null}

      <div className="chub__tabs" role="tablist">
        {TABS.map((t) => {
          const why = tabBlocked(t.id);
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="chub__tab"
              data-on={tab === t.id || undefined}
              data-off={why ? true : undefined}
              title={why ?? t.hint}
              onClick={() => chooseTab(t.id)}
            >
              <span aria-hidden="true">{t.icon}</span> {t.label}
              <small>{why ? "여기선 못 고쳐요" : t.hint}</small>
            </button>
          );
        })}
      </div>

      <div className="chub__body">
        {blocked ? (
          <p className="hint">🔒 {blocked}</p>
        ) : tab === "art" ? (
          <CharArt char={cur} entry={a[cur.stillKey!]} spec={slotByKey[cur.stillSpec ?? ""]} onChange={setEntry} onGoAssets={onGoAssets} />
        ) : tab === "anim" ? (
          <AnimationStudio charKey={cur.key} embedded />
        ) : tab === "size" ? (
          <WorldSizePanel only={cur.npcId ?? "hero"} />
        ) : tab === "know" ? (
          <NpcAgentPanel npc={cur.npcId!} label={cur.label} />
        ) : tab === "deco" ? (
          /* 방향 그림을 **이 자리에서** 올릴 수 있게 매니페스트를 내려 준다 — 옆모습을 보다가
             "이 방향 그림이 없네"를 알게 되는 곳이 바로 여기다. 저장은 아래 고정 바 하나로 모인다. */
          <OverlayFitPanel
            lookKey={cur.key}
            assets={a}
            partSpec={slotByKey["hero.part"]?.spec}
            onUpload={setEntry}
          />
        ) : (
          <NpcLinePanel focusNpc={cur.npcId} />
        )}
      </div>
    </section>
  );
}

/** 🖼️ 그림 탭 — 서 있는 모습 한 장. 방향별 그림은 움직임 탭이 다룬다(중복 업로드 방지). */
function CharArt({
  char,
  entry,
  spec,
  onChange,
  onGoAssets,
}: {
  char: CharDef;
  entry?: ManifestEntry;
  spec?: AssetSlot;
  onChange: (key: string, entry: ManifestEntry | null) => void;
  onGoAssets?: () => void;
}) {
  if (!char.stillKey || !spec) {
    return (
      <p className="hint">
        규격을 불러오지 못했어요.{" "}
        {onGoAssets ? (
          <button className="btn btn--sm" onClick={onGoAssets}>
            [🖼️ 그림 전체 목록]에서 올리기
          </button>
        ) : null}
      </p>
    );
  }
  return (
    <>
      <p className="hint">
        아이에게 보이는 <b>서 있는 모습</b> 한 장이에요. 배경이 비치는 투명 그림(PNG·WebP)을
        올려주세요.
      </p>
      <SlotUploader
        slotKey={char.stillKey}
        label={`${char.label} · 서 있는 모습`}
        spec={spec.spec}
        entry={entry}
        onChange={onChange}
        alt={char.label}
      />
      <p className="hint">
        걸어다니는 모습({Object.values(DIR_LABEL).join("·")})은 <b>[🎞️ 움직임]</b> 에서 만들어요.
      </p>
    </>
  );
}

/** 움직임 탭이 다루는 슬롯 키 — 도우미·검색이 같은 값을 쓰게 밖으로 뺀다. */
export { charAnimKeys };
